import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Share2, Bookmark, CheckCircle2, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PostComments from "@/components/features/PostComments";

export default function PostCard({ post: initial, delay = 0, onDeleted }) {
  const [post, setPost] = useState(initial);
  const [voted, setVoted] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showHeartAnim, setShowHeartAnim] = useState(false);
  const { user } = useAuth();
  
  const lastTapRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startLongPress = useCallback(() => {
    // Only the post owner can delete it
    if (!user || String(post.author?.id) !== String(user.id)) {
      return;
    }

    longPressTriggeredRef.current = false;

    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setShowDelete(true);

      // Small vibration on supported mobile devices
      if (navigator.vibrate) {
        navigator.vibrate(40);
      }
    }, 700);
  }, [user, post.author?.id]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const handleDelete = async () => {
    if (!user || String(post.author?.id) !== String(user.id)) {
      toast.error("You can only delete your own Pluse");
      return;
    }

    setDeleting(true);

    try {
      await api.delete(`/posts/${post.id}`);

      setShowDelete(false);

      toast.success("Pluse deleted");

      // Tell parent/feed that this post was deleted
      if (typeof onDeleted === "function") {
        onDeleted(post.id);
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Failed to delete Pluse"
      );
    } finally {
      setDeleting(false);
    }
  };

  const like = useCallback(async () => {
    if (!user) { toast.error("Login to like posts"); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPost((p) => ({ ...p, liked: data.liked, likes: p.likes + (data.liked ? 1 : -1) }));
    } catch (e) { toast.error("Failed"); }
  }, [user, post.id]);

  const handleDoubleTapLike = useCallback((e) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    if (e.target.closest("button") || e.target.closest("a") || e.target.closest("input")) {
      return;
    }

    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      if (!post.liked) {
        like();
      }
      setShowHeartAnim(true);
      setTimeout(() => setShowHeartAnim(false), 700);
    }
    lastTapRef.current = now;
  }, [post.liked, like]);

  const vote = async (idx) => {
    if (!user) { toast.error("Login to vote"); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/vote`, { option_index: idx });
      setPost((p) => ({ ...p, poll: data.poll }));
      setVoted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Already voted");
      setVoted(true);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/pluse/${encodeURIComponent(post.id)}`;
    const authorName = post.author?.name || post.author?.username || "DrkC Creator";
    const preview = post.content?.trim()
      ? post.content.trim().slice(0, 120)
      : "Check out this Pluse on DrkC.";
    const title = `${authorName} shared a Pluse on DrkC`;
    const text = `Check out this Pluse on DrkC:\n\n${preview}`;

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
        // If native sharing fails, try copying the direct link.
      }
    }

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(`${text}\n\n${url}`);
      } else {
        const input = document.createElement("textarea");
        input.value = `${text}\n\n${url}`;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        input.remove();
        if (!copied) throw new Error("Clipboard copy failed");
      }
      toast.success("Pluse link copied");
    } catch (e) {
      console.error("Pluse share failed:", e);
      toast.error("Unable to share or copy the link. Please try again.");
    }
  };

  const save = useCallback(async () => {
    if (!user) { toast.error("Login to save posts"); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/save`);
      setPost((p) => ({ ...p, saved: data.saved }));
      toast.success(data.saved ? "Saved to your bookmarks" : "Removed from saved");
    } catch { toast.error("Failed to save"); }
  }, [user, post.id]);

  // Helper to parse and render content with #hashtags and @mentions as required custom links
  const renderFormattedContent = (text) => {
    if (!text) return null;
    const parts = text.split(/(@[A-Za-z0-9_]+|#[A-Za-z0-9_]+)/g);

    return parts.map((part, index) => {
      if (part.startsWith("#")) {
        const tag = part.slice(1);
        return (
          <a
            key={index}
            href={`/hashtag/${encodeURIComponent(tag)}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[#6EE7FF] hover:underline font-medium transition-colors"
          >
            {part}
          </a>
        );
      } else if (part.startsWith("@")) {
        const username = part.slice(1);
        return (
          <a
            key={index}
            href={`/community/@${username}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[#9387FE] hover:underline font-medium transition-colors"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const totalVotes = post.poll?.reduce((s, o) => s + o.votes, 0) || 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -50px 0px" }}
      transition={{ duration: 0.3, delay, ease: "easeOut" }}
      data-testid={`post-card-${post.id}`}
      onClick={handleDoubleTapLike}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      className="glass rounded-3xl p-4 sm:p-6 relative w-full overflow-hidden cursor-default will-change-transform select-none"
    >
      <AnimatePresence>
        {showDelete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md rounded-3xl"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="glass-strong border border-white/10 rounded-2xl p-5 w-[280px] shadow-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  <h3 className="font-semibold text-white">
                    Delete Pluse?
                  </h3>
                </div>

                <button
                  onClick={() => setShowDelete(false)}
                  className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-white/60 mb-5">
                This action cannot be undone.
              </p>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowDelete(false)}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl text-xs text-white/70 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold transition-colors"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.12}
        onDragEnd={(e, info) => {
          if (info.offset.x < -50) {
            // Swipe Left -> Toggle Reply Window
            setShowComments((prev) => !prev);
          } else if (info.offset.x > 50) {
            // Swipe Right -> Toggle Save / Bookmark
            save();
          }
        }}
        className="flex items-start gap-3 sm:gap-4 w-full touch-pan-y relative"
      >
        <AnimatePresence>
          {showHeartAnim && (
            <motion.div
              initial={{ scale: 0.4, opacity: 0, y: 10 }}
              animate={{ scale: 1.1, opacity: 1, y: 0 }}
              exit={{ scale: 1.4, opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute inset-0 m-auto w-20 h-20 flex items-center justify-center z-20 pointer-events-none"
            >
              <Heart className="w-20 h-20 fill-[#FF6AD5] text-[#FF6AD5] drop-shadow-2xl" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Vertical Left Action Bar (Hardware Accelerated & Optimized for Mobile/PC) */}
        <div className="flex flex-col items-center gap-2 py-2 px-1.5 border-r border-white/10 pr-3 text-white/70 shrink-0 select-none">
          <button
            onClick={like}
            data-testid={`post-like-${post.id}`}
            title="Like"
            className={`p-2 rounded-xl hover:bg-white/10 transition-colors duration-150 flex flex-col items-center gap-0.5 ${post.liked ? "text-[#FF6AD5]" : "hover:text-[#FF6AD5]"}`}
          >
            <Heart className={`w-4 h-4 transition-transform active:scale-125 ${post.liked ? "fill-[#FF6AD5]" : ""}`} />
            <span className="text-[10px] font-medium">{post.likes || 0}</span>
          </button>

          <button
            onClick={() => setShowComments(!showComments)}
            data-testid={`post-comments-toggle-${post.id}`}
            title="Reply / Comment"
            className={`p-2 rounded-xl hover:bg-white/10 transition-colors duration-150 flex flex-col items-center gap-0.5 ${showComments ? "text-[#6EE7FF]" : "hover:text-[#6EE7FF]"}`}
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-[10px] font-medium">{post.comments_count || 0}</span>
          </button>

          <button
            onClick={save}
            data-testid={`post-save-${post.id}`}
            title={post.saved ? "Saved" : "Save"}
            className={`p-2 rounded-xl hover:bg-white/10 transition-colors duration-150 ${post.saved ? "text-[#FFD54A]" : "hover:text-[#FFD54A]"}`}
          >
            <Bookmark className={`w-4 h-4 transition-transform active:scale-125 ${post.saved ? "fill-[#FFD54A]" : ""}`} />
          </button>

          <button
            onClick={share}
            data-testid={`post-share-${post.id}`}
            title="Share"
            className="p-2 rounded-xl hover:bg-white/10 hover:text-white transition-colors duration-150 flex flex-col items-center gap-0.5"
          >
            <Share2 className="w-4 h-4" />
            {post.shares > 0 && <span className="text-[10px] font-medium text-white/50">{post.shares}</span>}
          </button>
        </div>

        {/* Main Post Content Area */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-4">
            {post.author && (
              <Link to={`/community/${post.author.id}`} className="flex items-center gap-3 group min-w-0">
                <img src={post.author.avatar_url} alt="" className="w-11 h-11 rounded-full border border-white/10 shrink-0 object-cover" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate">
                    <span className="font-semibold text-sm group-hover:gradient-text transition-colors truncate">{post.author.name}</span>
                    {post.author.verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#6EE7FF] shrink-0" />}
                  </div>
                  <div className="text-xs text-white/55 truncate">@{post.author.username} &middot; {timeAgo(post.created_at)}</div>
                </div>
              </Link>
            )}
            {post.kind === "announcement" && (
              <span className="ml-auto px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold shrink-0" style={{ background: "linear-gradient(135deg,#FFD54A,#FF6AD5)", color: "#0a0a0a" }}>
                Announcement
              </span>
            )}
          </div>

          <div className="whitespace-pre-wrap text-white/90 leading-relaxed mb-3 text-sm sm:text-base break-words">
            {renderFormattedContent(post.content)}
          </div>

          {post.media_url && (
            <img src={post.media_url} alt="" loading="lazy" className="w-full rounded-2xl border border-white/10 mb-3 max-h-[480px] object-cover" />
          )}

          {post.poll && (
            <div className="space-y-2 mb-3">
              {post.poll.map((opt, i) => {
                const pct = totalVotes ? Math.round((opt.votes / totalVotes) * 100) : 0;
                return (
                  <button
                    key={i}
                    onClick={() => vote(i)}
                    disabled={voted}
                    data-testid={`poll-option-${post.id}-${i}`}
                    className="w-full text-left relative px-4 py-3 rounded-xl border border-white/10 hover:border-white/20 overflow-hidden group disabled:cursor-default transition-all duration-200"
                  >
                    <span className="absolute inset-y-0 left-0 rounded-xl transition-all duration-500" style={{ width: `${pct}%`, background: "linear-gradient(90deg, rgba(147,135,254,0.35), rgba(255,106,213,0.2))" }} />
                    <span className="relative flex items-center justify-between text-sm">
                      <span className="font-medium truncate mr-2">{opt.option}</span>
                      <span className="text-white/70 text-xs font-mono shrink-0">{pct}% &middot; {opt.votes}</span>
                    </span>
                  </button>
                );
              })}
              <div className="text-xs text-white/40">{totalVotes} votes</div>
            </div>
          )}

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {post.tags.map((t) => (
                <a key={t} href={`/pluse?tag=${t.replace('#', '')}`} onClick={(e) => e.stopPropagation()} className="text-xs text-[#6EE7FF]/80 hover:text-[#6EE7FF] hover:underline transition-colors">
                  {t.startsWith('#') ? t : `#${t}`}
                </a>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Reply / Comment Section Drawer with Smooth Collapse/Expand Transition */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0, scaleY: 0.95 }}
            animate={{ opacity: 1, height: "auto", scaleY: 1 }}
            exit={{ opacity: 0, height: 0, scaleY: 0.95 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="mt-4 pt-4 border-t border-white/10 overflow-hidden origin-top"
          >
            <PostComments postId={post.id} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}